/**
 * Palette-aanpassingen per ArchiMate-weergavemodus.
 *
 * Na de type-merge (fase 4) staan de achttien laag-specifieke types op
 * `fase1_active: false` en zijn de zes generieke Common-Domain-types actief.
 * Het palette moet dan van vorm wisselen:
 *
 * - **v3.1** — de vertrouwde lijst. "Bedrijfsproces" is dan geen eigen type
 *   meer maar een **synthetische entry**: label/laag/kleur/icoon van het oude
 *   type, maar bij het slepen komt er `archimate.Process` +
 *   `«archimate31.BusinessProcess»` uit. Dat is exact het bestaande
 *   `default_stereotypes`-mechanisme dat C4 en Sharp al gebruiken — er wordt
 *   dus niets nieuws uitgevonden, alleen hergebruikt.
 * - **v4** — de generieke types; de laag-specifieke verdwijnen uit beeld.
 *
 * **Waarom de `active`-checks**: zolang fase 4 niet gedraaid heeft zijn de
 * oude types nog gewoon actief en de generieke nog niet. Beide takken
 * hieronder zijn dan een no-op, waardoor het palette in béide standen
 * identiek is aan vandaag. Dat maakt fase 3 los verifieerbaar: shippen, de
 * schakelaar omzetten, en er hoort niets te veranderen.
 *
 * Zie `docs/architecture/archimate4-migratie.md` §4 fase 3.
 */
import {
  type ElementType,
  isV4GenericType,
  type RegistryRuntime,
  TYPE_MAP_31_TO_4,
} from "@ae/domain";
import type { DisplayMode } from "../../state/displayMode";

/** Een laag-specifiek type dat als palette-entry gesynthetiseerd wordt uit
 *  het compat-stereotype. `elementType` is de OUDE (inactieve) definitie —
 *  die levert label, laag, kleur en icoon. */
export interface LegacyPaletteEntry {
  elementType: ElementType;
  /** Het generieke type dat daadwerkelijk aangemaakt wordt bij drop. */
  dragTypeId: string;
  /** Het compat-stereotype dat de 3.1-identiteit meegeeft. */
  defaultStereotypes: string[];
}

export interface DisplayModePaletteAdjustments {
  /** Type-ids die in deze modus niet in de laag-groepen horen. */
  hiddenTypeIds: Set<string>;
  /** Synthetische 3.1-entries, gegroepeerd per (oude) laag. */
  legacyEntriesByLayer: Map<string, LegacyPaletteEntry[]>;
}

export function displayModePaletteAdjustments(
  reg: RegistryRuntime,
  mode: DisplayMode,
): DisplayModePaletteAdjustments {
  const hiddenTypeIds = new Set<string>();
  const legacyEntriesByLayer = new Map<string, LegacyPaletteEntry[]>();

  const isActive = (typeId: string): boolean => reg.getElementType(typeId)?.fase1_active === true;

  // In v4-modus verbergen we NIETS. De achttien laag-specifieke types
  // verdwijnen na fase 4 vanzelf uit het palette omdat de migratie ze op
  // `fase1_active: false` zet; ze hier ook nog eens wegfilteren voegt niets
  // toe en ging in de eerste versie juist mis — vóór de flip verdwenen
  // Constraint / Contract / Gap / Representation dan meteen uit v4-modus,
  // terwijl hun vervanger nog niet bestond (gevonden 2026-08-09).
  if (mode === "v4") return { hiddenTypeIds, legacyEntriesByLayer };

  for (const [legacyTypeId, migration] of Object.entries(TYPE_MAP_31_TO_4)) {
    // ALLEEN de zes nieuwe Common-Domain-types mogen in v3.1-modus verborgen
    // worden. De vier andere merge-doelen (Requirement, BusinessObject,
    // Assessment, DataObject) zijn gewone ArchiMate-types die allang
    // bestonden en die de modelleur gewoon moet kunnen pakken — dát was de
    // regressie: in v3.1, de standaardmodus, vielen ze uit het palette.
    if (isV4GenericType(migration.typeId) && isActive(migration.typeId)) {
      hiddenTypeIds.add(migration.typeId);
    }

    // Synthetiseer het oude type zodra 't niet meer zelf actief is (= ná de
    // flip) en z'n vervanger er wél is.
    if (isActive(legacyTypeId) || !isActive(migration.typeId)) continue;
    const legacyType = reg.getElementType(legacyTypeId);
    if (!legacyType) continue;
    const arr = legacyEntriesByLayer.get(legacyType.layer) ?? [];
    arr.push({
      elementType: legacyType,
      dragTypeId: migration.typeId,
      defaultStereotypes: [migration.stereotype],
    });
    legacyEntriesByLayer.set(legacyType.layer, arr);
  }

  return { hiddenTypeIds, legacyEntriesByLayer };
}
